import {useCallback, useEffect, useMemo} from "react";
import {useLocation} from "react-router";
import {css} from "@panda/css";

import {IOption, Select} from "@pg-design/select-module";
import {Tabs} from "@pg-design/tabs-module";
import {RouteChangeValidator} from "@pg-mono/data-fetcher";

import {offerUrlConstants} from "../../../offer/helpers/friendly_offer_list/offer_url_constants";
import {SearchSort} from "../../../offer/list/constants/SearchSort";
import {OfferType} from "../../../offer/types/OfferType";
import {useAppDispatch, useAppSelector} from "../../../store/hooks";
import {useUpdateUrlParams} from "../../hooks/use_update_url_params";
import {resetActiveOffersPage, setVendorActiveOfferType, setVendorActiveRegion, setVendorActiveSort} from "../../state/vendor_offer_list_slice";
import {IVendorDetail} from "../../types/IVendorDetail";

const LOCATION_PARAM = "lokalizacja";
const TYPE_PARAM = "typ";

interface IVendorOfferSearchSectionProps {
    vendor: IVendorDetail;
    cities: {
        id: number;
        name: string;
        slug: string;
    }[];
    hasActiveFlats: boolean;
    hasActiveHouses: boolean;
    hasActiveCommercials: boolean;
}

interface IPropertyTypeOption extends IOption<string> {
    offerType: OfferType | null;
    urlSlug: string | null;
}

interface ISortOption extends IOption<SearchSort> {}

const PROPERTY_TYPE_OPTIONS: IPropertyTypeOption[] = [
    {value: "all", label: "Mieszkania i domy", offerType: null, urlSlug: null},
    {value: "flats", label: "Mieszkania", offerType: OfferType.FLAT, urlSlug: "mieszkania"},
    {value: "houses", label: "Domy", offerType: OfferType.HOUSE, urlSlug: "domy"},
    {value: "commercials", label: "Lokale użytkowe", offerType: OfferType.COMMERCIAL, urlSlug: "lokale-uzytkowe"}
];

const SORT_OPTIONS: ISortOption[] = [
    {value: SearchSort.DEFAULT, label: "Polecane"},
    {value: SearchSort.LATEST, label: "Najnowsze"},
    {value: SearchSort.PRICE, label: "Najtańsze"},
    {value: SearchSort.PRICE_M2, label: "Najtańsze (za m²)"},
    {value: SearchSort.CONSTRUCTION_DATE, label: "Termin oddania"}
];

const getUrlSlugForOfferType = (offerType: OfferType | null): string | null => {
    if (!offerType) {
        return null;
    }

    const entry = Object.entries(offerUrlConstants.types).find(([, value]) => value === offerType);
    return entry ? entry[0] : null;
};

/** Builds search string for a given location slug while preserving other params (e.g. typ). Used for tab hrefs (SEO). */
const getSearchStringForRegion = (currentSearch: string, locationSlug: string | null, typSlug: string | null) => {
    const current = new URLSearchParams(currentSearch);
    const next = new URLSearchParams();

    current.forEach((value, key) => {
        if (key !== LOCATION_PARAM && key !== TYPE_PARAM) {
            next.set(key, value);
        }
    });

    if (locationSlug) {
        next.set(LOCATION_PARAM, locationSlug);
    }

    if (typSlug) {
        next.set(TYPE_PARAM, typSlug);
    }

    return next.toString();
};

export const VendorDetailSearchFilters = (props: IVendorOfferSearchSectionProps) => {
    const {cities, hasActiveFlats, hasActiveHouses, hasActiveCommercials} = props;
    const dispatch = useAppDispatch();
    const location = useLocation();
    const {updateUrlParams} = useUpdateUrlParams();

    useEffect(() => {
        RouteChangeValidator.registerIgnoredParam(LOCATION_PARAM);
        RouteChangeValidator.registerIgnoredParam(TYPE_PARAM);
    }, []);

    const selectedRegion = useAppSelector((state) => state.vendorDetailViewSlice.activeOffersListParams.region);
    const selectedOfferType = useAppSelector((state) => state.vendorDetailViewSlice.activeOffersListParams.offerType);
    const selectedSort = useAppSelector((state) => state.vendorDetailViewSlice.activeOffersListParams.sort);

    const availablePropertyTypeOptions = useMemo(() => {
        return PROPERTY_TYPE_OPTIONS.map((option) => {
            let isDisabled = false;

            if (option.value === "flats" && !hasActiveFlats) {
                isDisabled = true;
            } else if (option.value === "houses" && !hasActiveHouses) {
                isDisabled = true;
            } else if (option.value === "commercials" && !hasActiveCommercials) {
                isDisabled = true;
            } else if (option.value === "all" && !hasActiveFlats && !hasActiveHouses) {
                isDisabled = true;
            }

            return {...option, isDisabled};
        });
    }, [hasActiveFlats, hasActiveHouses, hasActiveCommercials]);

    const currentPropertyTypeOption = useMemo(() => {
        if (!selectedOfferType) {
            return availablePropertyTypeOptions[0];
        }
        return availablePropertyTypeOptions.find((opt) => opt.offerType === selectedOfferType) || availablePropertyTypeOptions[0];
    }, [selectedOfferType, availablePropertyTypeOptions]);

    const currentSortOption = useMemo(() => {
        return SORT_OPTIONS.find((opt) => opt.value === selectedSort) || SORT_OPTIONS[0];
    }, [selectedSort]);

    const syncFiltersWithUrl = (regionId: number | null, offerType: OfferType | null) => {
        dispatch(resetActiveOffersPage());

        const selectedCity = regionId ? cities.find((city) => city.id === regionId) : null;
        updateUrlParams({
            lokalizacja: selectedCity?.slug ?? null,
            typ: getUrlSlugForOfferType(offerType)
        });
    };

    const handleRegionChange = useCallback(
        (regionId: number) => {
            const regionIdNormalized = regionId === -1 ? null : regionId;

            if (regionIdNormalized === selectedRegion) {
                return;
            }

            dispatch(setVendorActiveRegion(regionIdNormalized));
            syncFiltersWithUrl(regionIdNormalized, selectedOfferType);
        },
        [selectedRegion, selectedOfferType]
    );

    const handlePropertyTypeChange = useCallback(
        (option: unknown) => {
            const typedOption = option as IPropertyTypeOption | null;
            if (!typedOption) {
                return;
            }

            dispatch(setVendorActiveOfferType(typedOption.offerType));
            syncFiltersWithUrl(selectedRegion, typedOption.offerType);
        },
        [selectedRegion]
    );

    const handleSortChange = useCallback(
        (option: unknown) => {
            const typedOption = option as ISortOption | null;
            if (!typedOption) return;

            dispatch(setVendorActiveSort(typedOption.value));
            dispatch(resetActiveOffersPage());
        },
        [dispatch]
    );

    const typSlug = useMemo(() => getUrlSlugForOfferType(selectedOfferType), [selectedOfferType]);

    const regionTabsWithHrefs = useMemo(() => {
        const basePath = location.pathname;
        const search = location.search ?? "";
        const queryForAll = getSearchStringForRegion(search, null, typSlug);

        return [
            {
                title: "Wszystkie",
                value: -1 as number,
                href: queryForAll ? `${basePath}?${queryForAll}` : basePath
            },
            ...cities.map((region) => {
                const query = getSearchStringForRegion(search, region.slug, typSlug);
                return {
                    title: region.name,
                    value: region.id,
                    href: `${basePath}${query ? `?${query}` : ""}`
                };
            })
        ];
    }, [location.pathname, location.search, cities, typSlug]);

    return (
        <section className={sectionStyle}>
            {cities.length > 1 && (
                <div className={tabsWrapperStyle}>
                    <Tabs<number>
                        activeColor="gray"
                        variant="contained"
                        fullWidth={false}
                        onChange={handleRegionChange}
                        titleClassName={tabTitleStyle}
                        value={selectedRegion ?? -1}
                        tabs={regionTabsWithHrefs}
                    />
                </div>
            )}

            <div className={filtersRowStyle}>
                <div className={selectWrapperStyle}>
                    <Select
                        labelContent="Typ nieruchomości"
                        value={currentPropertyTypeOption}
                        onChange={handlePropertyTypeChange}
                        options={availablePropertyTypeOptions}
                        isOptionDisabled={(option) => (option as IPropertyTypeOption & {isDisabled?: boolean}).isDisabled === true}
                    />
                </div>

                <div className={selectWrapperStyle}>
                    <Select labelContent="Sortuj" value={currentSortOption} onChange={handleSortChange} options={SORT_OPTIONS} />
                </div>
            </div>
        </section>
    );
};

const sectionStyle = css({
    pt: 4,
    pb: 4
});

const tabsWrapperStyle = css({
    mb: 3,
    overflowX: "auto",
    overflowY: "hidden"
});

const tabTitleStyle = css({
    paddingLeft: 3,
    paddingRight: 3
});

const filtersRowStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 2,

    md: {
        flexDirection: "row",
        alignItems: "flex-end",
        justifyContent: "space-between"
    }
});

const selectWrapperStyle = css({
    width: "100%",

    md: {
        maxWidth: "280px"
    }
});
import {useCallback, useEffect, useMemo} from "react";
import {useLocation} from "react-router";
import {css} from "@panda/css";

import {IOption, Select} from "@pg-design/select-module";
import {Tabs} from "@pg-design/tabs-module";
import {RouteChangeValidator} from "@pg-mono/data-fetcher";

import {offerUrlConstants} from "../../../offer/helpers/friendly_offer_list/offer_url_constants";
import {SearchSort} from "../../../offer/list/constants/SearchSort";
import {OfferType} from "../../../offer/types/OfferType";
import {useAppDispatch, useAppSelector} from "../../../store/hooks";
import {useUpdateUrlParams} from "../../hooks/use_update_url_params";
import {resetActiveOffersPage, setVendorActiveOfferType, setVendorActiveRegion, setVendorActiveSort} from "../../state/vendor_offer_list_slice";
import {IVendorDetail} from "../../types/IVendorDetail";

const LOCATION_PARAM = "lokalizacja";
const TYPE_PARAM = "typ";

interface IVendorOfferSearchSectionProps {
    vendor: IVendorDetail;
    cities: {
        id: number;
        name: string;
        slug: string;
    }[];
    hasActiveFlats: boolean;
    hasActiveHouses: boolean;
    hasActiveCommercials: boolean;
}

interface IPropertyTypeOption extends IOption<string> {
    offerType: OfferType | null;
    urlSlug: string | null;
}

interface ISortOption extends IOption<SearchSort> {}

const PROPERTY_TYPE_OPTIONS: IPropertyTypeOption[] = [
    {value: "all", label: "Mieszkania i domy", offerType: null, urlSlug: null},
    {value: "flats", label: "Mieszkania", offerType: OfferType.FLAT, urlSlug: "mieszkania"},
    {value: "houses", label: "Domy", offerType: OfferType.HOUSE, urlSlug: "domy"},
    {value: "commercials", label: "Lokale użytkowe", offerType: OfferType.COMMERCIAL, urlSlug: "lokale-uzytkowe"}
];

const SORT_OPTIONS: ISortOption[] = [
    {value: SearchSort.DEFAULT, label: "Polecane"},
    {value: SearchSort.LATEST, label: "Najnowsze"},
    {value: SearchSort.PRICE, label: "Najtańsze"},
    {value: SearchSort.PRICE_M2, label: "Najtańsze (za m²)"},
    {value: SearchSort.CONSTRUCTION_DATE, label: "Termin oddania"}
];

const getUrlSlugForOfferType = (offerType: OfferType | null): string | null => {
    if (!offerType) {
        return null;
    }

    const entry = Object.entries(offerUrlConstants.types).find(([, value]) => value === offerType);
    return entry ? entry[0] : null;
};

/** Builds search string for a given location slug while preserving other params (e.g. typ). Used for tab hrefs (SEO). */
const getSearchStringForRegion = (currentSearch: string, locationSlug: string | null, typSlug: string | null) => {
    const current = new URLSearchParams(currentSearch);
    const next = new URLSearchParams();

    current.forEach((value, key) => {
        if (key !== LOCATION_PARAM && key !== TYPE_PARAM) {
            next.set(key, value);
        }
    });

    if (locationSlug) {
        next.set(LOCATION_PARAM, locationSlug);
    }

    if (typSlug) {
        next.set(TYPE_PARAM, typSlug);
    }

    return next.toString();
};

export const VendorDetailSearchFilters = (props: IVendorOfferSearchSectionProps) => {
    const {cities, hasActiveFlats, hasActiveHouses, hasActiveCommercials} = props;
    const dispatch = useAppDispatch();
    const location = useLocation();
    const {updateUrlParams} = useUpdateUrlParams();

    useEffect(() => {
        RouteChangeValidator.registerIgnoredParam(LOCATION_PARAM);
        RouteChangeValidator.registerIgnoredParam(TYPE_PARAM);
    }, []);

    const selectedRegion = useAppSelector((state) => state.vendorDetailViewSlice.activeOffersListParams.region);
    const selectedOfferType = useAppSelector((state) => state.vendorDetailViewSlice.activeOffersListParams.offerType);
    const selectedSort = useAppSelector((state) => state.vendorDetailViewSlice.activeOffersListParams.sort);

    const availablePropertyTypeOptions = useMemo(() => {
        return PROPERTY_TYPE_OPTIONS.map((option) => {
            let isDisabled = false;

            if (option.value === "flats" && !hasActiveFlats) {
                isDisabled = true;
            } else if (option.value === "houses" && !hasActiveHouses) {
                isDisabled = true;
            } else if (option.value === "commercials" && !hasActiveCommercials) {
                isDisabled = true;
            } else if (option.value === "all" && !hasActiveFlats && !hasActiveHouses) {
                isDisabled = true;
            }

            return {...option, isDisabled};
        });
    }, [hasActiveFlats, hasActiveHouses, hasActiveCommercials]);

    const currentPropertyTypeOption = useMemo(() => {
        if (!selectedOfferType) {
            return availablePropertyTypeOptions[0];
        }
        return availablePropertyTypeOptions.find((opt) => opt.offerType === selectedOfferType) || availablePropertyTypeOptions[0];
    }, [selectedOfferType, availablePropertyTypeOptions]);

    const currentSortOption = useMemo(() => {
        return SORT_OPTIONS.find((opt) => opt.value === selectedSort) || SORT_OPTIONS[0];
    }, [selectedSort]);

    const syncFiltersWithUrl = (regionId: number | null, offerType: OfferType | null) => {
        dispatch(resetActiveOffersPage());

        const selectedCity = regionId ? cities.find((city) => city.id === regionId) : null;
        updateUrlParams({
            lokalizacja: selectedCity?.slug ?? null,
            typ: getUrlSlugForOfferType(offerType)
        });
    };

    const handleRegionChange = useCallback(
        (regionId: number) => {
            const regionIdNormalized = regionId === -1 ? null : regionId;

            if (regionIdNormalized === selectedRegion) {
                return;
            }

            dispatch(setVendorActiveRegion(regionIdNormalized));
            syncFiltersWithUrl(regionIdNormalized, selectedOfferType);
        },
        [selectedRegion, selectedOfferType]
    );

    const handlePropertyTypeChange = useCallback(
        (option: unknown) => {
            const typedOption = option as IPropertyTypeOption | null;
            if (!typedOption) {
                return;
            }

            dispatch(setVendorActiveOfferType(typedOption.offerType));
            syncFiltersWithUrl(selectedRegion, typedOption.offerType);
        },
        [selectedRegion]
    );

    const handleSortChange = useCallback(
        (option: unknown) => {
            const typedOption = option as ISortOption | null;
            if (!typedOption) return;

            dispatch(setVendorActiveSort(typedOption.value));
            dispatch(resetActiveOffersPage());
        },
        [dispatch]
    );

    const typSlug = useMemo(() => getUrlSlugForOfferType(selectedOfferType), [selectedOfferType]);

    const regionTabsWithHrefs = useMemo(() => {
        const basePath = location.pathname;
        const search = location.search ?? "";
        const queryForAll = getSearchStringForRegion(search, null, typSlug);

        return [
            {
                title: "Wszystkie",
                value: -1 as number,
                href: queryForAll ? `${basePath}?${queryForAll}` : basePath
            },
            ...cities.map((region) => {
                const query = getSearchStringForRegion(search, region.slug, typSlug);
                return {
                    title: region.name,
                    value: region.id,
                    href: `${basePath}${query ? `?${query}` : ""}`
                };
            })
        ];
    }, [location.pathname, location.search, cities, typSlug]);

    return (
        <section className={sectionStyle}>
            {cities.length > 1 && (
                <div className={tabsWrapperStyle}>
                    <Tabs<number>
                        activeColor="gray"
                        variant="contained"
                        fullWidth={false}
                        onChange={handleRegionChange}
                        titleClassName={tabTitleStyle}
                        value={selectedRegion ?? -1}
                        tabs={regionTabsWithHrefs}
                    />
                </div>
            )}

            <div className={filtersRowStyle}>
                <div className={selectWrapperStyle}>
                    <Select
                        labelContent="Typ nieruchomości"
                        value={currentPropertyTypeOption}
                        onChange={handlePropertyTypeChange}
                        options={availablePropertyTypeOptions}
                        isOptionDisabled={(option) => (option as IPropertyTypeOption & {isDisabled?: boolean}).isDisabled === true}
                    />
                </div>

                <div className={selectWrapperStyle}>
                    <Select labelContent="Sortuj" value={currentSortOption} onChange={handleSortChange} options={SORT_OPTIONS} />
                </div>
            </div>
        </section>
    );
};

const sectionStyle = css({
    pt: 4,
    pb: 4
});

const tabsWrapperStyle = css({
    mb: 3,
    overflowX: "auto",
    overflowY: "hidden"
});

const tabTitleStyle = css({
    paddingLeft: 3,
    paddingRight: 3
});

const filtersRowStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 2,

    md: {
        flexDirection: "row",
        alignItems: "flex-end",
        justifyContent: "space-between"
    }
});

const selectWrapperStyle = css({
    width: "100%",

    md: {
        maxWidth: "280px"
    }
});
